import React from "react";
import { useUiLangStore } from "../../../../stores/uiLang";
import { peT } from "../../../../i18n/peLesson";

/**
 * LessonErrorBoundary — wraps the locked-sequence lesson UI so a render throw
 * (e.g. the reader-first step crash) degrades to a VISIBLE fallback instead of
 * a blank white screen, and surfaces the real throwing component for diagnosis.
 *
 * componentDidCatch logs with a stable `[LessonErrorBoundary]` prefix so the
 * crash is unambiguous in the console vs the known "No Google access token"
 * red herring — copy that block + map the frames against index-*.js.map.
 */
interface Props {
  children: React.ReactNode;
}
interface State {
  error: Error | null;
}

export default class LessonErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(
      "[LessonErrorBoundary] lesson render crashed\n" +
        "message: " +
        (error?.message ?? String(error)) +
        "\nstack: " +
        (error?.stack ?? "(none)") +
        "\ncomponentStack: " +
        (info?.componentStack ?? "(none)"),
    );
  }

  render(): React.ReactNode {
    if (this.state.error) {
      // Run E2 J5: the lesson page has already set the shared UI language
      // to the assignment language, so the fallback follows it.
      const t = peT(useUiLangStore.getState().lang);
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="text-center max-w-md rounded-xl bg-white border border-gray-200 p-8 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900">
              {t.lessonCrashTitle}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {t.lessonCrashBody}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 rounded-lg bg-indigo-600 px-5 py-2.5 text-white font-semibold hover:bg-indigo-700"
            >
              {t.refresh}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
